/**
 * Sentinel Certification Platform data models + seeded catalog.
 *
 * These types and seed data power the certification UI (dashboard, catalog,
 * program detail, licenses, renewals, certificates, capstones, admin).
 * When Wix Online Programs / Pricing Plans / Members are configured, the
 * live API responses map onto these same shapes.
 */

export type UserRole =
  | "student"
  | "supervisor"
  | "manager"
  | "executive"
  | "agency_admin"
  | "enterprise_admin"
  | "platform_admin";

export type CertificationLevel = "Professional" | "Supervisor" | "Manager" | "Executive";

export type ProgramStatus = "not_started" | "in_progress" | "completed" | "expired";

export type LicenseType = "individual" | "agency" | "enterprise" | "renewal";

export type LicenseStatus = "active" | "expired";

export type OrgType =
  | "law_enforcement"
  | "healthcare"
  | "education"
  | "hospitality"
  | "culinary"
  | "manufacturing"
  | "transportation"
  | "defense"
  | "municipal"
  | "nonprofit";

/* ──────────────────────────────────────────────────────────── */
/* 1) User / Member                                              */
/* ──────────────────────────────────────────────────────────── */

export interface UserMember {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  organization_id?: string;
  active_licenses: string[];
  completed_certifications: string[];
  progress: Record<string, Record<string, { status: ProgramStatus; quiz_score?: number; capstone_status?: ProgramStatus }>>;
}

/* ──────────────────────────────────────────────────────────── */
/* 2) Certification Program                                      */
/* ──────────────────────────────────────────────────────────── */

export interface CertificationProgram {
  id: string;
  industry: string;
  level: CertificationLevel;
  code: string;
  title: string;
  description: string;
  linked_online_program_id: string;
  price_plan_id: string;
  price: number;
  currency: string;
  term: string;
  renewal_price_plan_id: string;
  renewal_price: number;
  image: string;
  modules: ProgramModuleData[];
  capstone: { id: string; title: string; description: string };
}

export interface ProgramModuleData {
  id: string;
  title: string;
  description: string;
  lessons: { id: string; title: string; content_ref: string; quiz_ref?: string }[];
}

/* ──────────────────────────────────────────────────────────── */
/* 3) Pricing Plan / License                                     */
/* ──────────────────────────────────────────────────────────── */

export interface PricingLicense {
  id: string;
  type: LicenseType;
  name: string;
  seats: number;
  industry: string;
  levels_covered: CertificationLevel[];
  renewal_included: boolean;
  term: string;
  status: LicenseStatus;
  price: number;
  currency: string;
  price_plan_id: string;
  start_date: string;
  end_date: string;
}

/* ──────────────────────────────────────────────────────────── */
/* 4) Organization / Agency                                     */
/* ──────────────────────────────────────────────────────────── */

export interface Organization {
  id: string;
  name: string;
  type: OrgType;
  seat_plans: string[];
  assigned_seats: Record<string, string[]>;
}

/* ──────────────────────────────────────────────────────────── */
/* 5) Certification Status                                       */
/* ──────────────────────────────────────────────────────────── */

export interface CertificationStatusRecord {
  user_id: string;
  program_id: string;
  status: ProgramStatus;
  exam_score?: number;
  capstone_status?: ProgramStatus;
  certificate_issued: boolean;
  badge_issued: boolean;
  renewal_due_date?: string;
  completion_date?: string;
  credential_code?: string;
}

/* ──────────────────────────────────────────────────────────── */
/* Seeded catalog                                               */
/* ──────────────────────────────────────────────────────────── */

const LEVELS: CertificationLevel[] = ["Professional", "Supervisor", "Manager", "Executive"];

const INDUSTRY_DEFS: { name: string; slug: string; image: string }[] = [
  { name: "Law Enforcement", slug: "law-enforcement", image: "https://images.pexels.com/photos/7714759/pexels-photo-7714759.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { name: "Healthcare", slug: "healthcare", image: "https://images.pexels.com/photos/6129691/pexels-photo-6129691.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { name: "Education", slug: "education", image: "https://images.pexels.com/photos/30731144/pexels-photo-30731144.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { name: "Hospitality", slug: "hospitality", image: "https://images.pexels.com/photos/7821349/pexels-photo-7821349.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { name: "Culinary", slug: "culinary", image: "https://images.pexels.com/photos/15671373/pexels-photo-15671373.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { name: "Manufacturing", slug: "manufacturing", image: "https://images.pexels.com/photos/7178310/pexels-photo-7178310.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { name: "Transportation", slug: "transportation", image: "https://images.pexels.com/photos/38169158/pexels-photo-38169158.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { name: "Defense", slug: "defense", image: "https://images.pexels.com/photos/12357501/pexels-photo-12357501.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { name: "Municipal Agencies", slug: "municipal", image: "https://images.pexels.com/photos/31283302/pexels-photo-31283302.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { name: "Nonprofits", slug: "nonprofit", image: "https://images.pexels.com/photos/6647027/pexels-photo-6647027.jpeg?auto=compress&cs=tinysrgb&w=600" },
];

const LEVEL_CODE_SUFFIX: Record<CertificationLevel, string> = {
  Professional: "-C",
  Supervisor: "-S",
  Manager: "-M",
  Executive: "-E",
};

const LEVEL_PRICE: Record<CertificationLevel, number> = {
  Professional: 495,
  Supervisor: 695,
  Manager: 895,
  Executive: 1195,
};

const LEVEL_RENEWAL: Record<CertificationLevel, number> = {
  Professional: 195,
  Supervisor: 245,
  Manager: 295,
  Executive: 395,
};

function buildModules(level: CertificationLevel): ProgramModuleData[] {
  const base: ProgramModuleData[] = [
    {
      id: "m1",
      title: "Sentinel Architecture Overview",
      description: "Monitoring, diagnostics, risk classification, intervention, verification, and reporting.",
      lessons: [
        { id: "l1", title: "The Six-Stage Sentinel Pipeline", content_ref: "content/m1/l1" },
        { id: "l2", title: "Multi-Domain Safety Principles", content_ref: "content/m1/l2" },
        { id: "l3", title: "Sentinel Templates & Tools", content_ref: "content/m1/l3", quiz_ref: "quiz/m1" },
      ],
    },
    {
      id: "m2",
      title: `${level}-Level Risk Classification`,
      description: `Industry-specific risk identification and classification for ${level} responsibilities.`,
      lessons: [
        { id: "l1", title: "Risk Taxonomy for Your Industry", content_ref: "content/m2/l1" },
        { id: "l2", title: "Diagnostic Profile Configuration", content_ref: "content/m2/l2", quiz_ref: "quiz/m2" },
      ],
    },
    {
      id: "m3",
      title: "Intervention & Verification",
      description: "Designing Sentinel-compliant intervention logic and verifying outcomes.",
      lessons: [
        { id: "l1", title: "Intervention Template Design", content_ref: "content/m3/l1" },
        { id: "l2", title: "Verification & Reporting", content_ref: "content/m3/l2", quiz_ref: "quiz/m3" },
      ],
    },
  ];
  if (level === "Manager" || level === "Executive") {
    base.push({
      id: "m4",
      title: "Organizational System Design",
      description: "Multi-domain integration, compliance mapping, and staff training oversight.",
      lessons: [
        { id: "l1", title: "Multi-Domain Integration", content_ref: "content/m4/l1" },
        { id: "l2", title: "Compliance Mapping & Audit", content_ref: "content/m4/l2", quiz_ref: "quiz/m4" },
      ],
    });
  }
  return base;
}

function buildPrograms(): CertificationProgram[] {
  const programs: CertificationProgram[] = [];
  for (const ind of INDUSTRY_DEFS) {
    const prefix = ind.slug.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 3);
    for (const level of LEVELS) {
      const code = `${prefix}-${level[0]}${LEVEL_CODE_SUFFIX[level]}`;
      programs.push({
        id: `${ind.slug}-${level.toLowerCase()}`,
        industry: ind.name,
        level,
        code,
        title: `${ind.name} ${level} (${code})`,
        description: `Sentinel ${level}-level certification for ${ind.name.toLowerCase()} professionals. Covers monitoring, diagnostics, risk classification, intervention, verification, and reporting tailored to ${ind.name.toLowerCase()} environments.`,
        linked_online_program_id: `wix-op-${ind.slug}-${level.toLowerCase()}`,
        price_plan_id: `wix-pp-${ind.slug}-${level.toLowerCase()}`,
        price: LEVEL_PRICE[level],
        currency: "$",
        term: "1-year access",
        renewal_price_plan_id: `wix-pp-${ind.slug}-${level.toLowerCase()}-renewal`,
        renewal_price: LEVEL_RENEWAL[level],
        image: ind.image,
        modules: buildModules(level),
        capstone: {
          id: `cap-${ind.slug}-${level.toLowerCase()}`,
          title: `${ind.name} ${level} Capstone Simulation`,
          description: `A scenario-based simulation validating your ability to apply the Sentinel Safety Architecture in a ${ind.name.toLowerCase()} environment at the ${level} level.`,
        },
      });
    }
  }
  return programs;
}

export const CERTIFICATION_PROGRAMS: CertificationProgram[] = buildPrograms();

export const CERTIFICATION_INDUSTRIES = INDUSTRY_DEFS.map((d) => d.name);

export const INDUSTRY_SLUGS = INDUSTRY_DEFS.map((d) => ({ name: d.name, slug: d.slug }));

export function getIndustrySlugByName(name: string): string | undefined {
  return INDUSTRY_DEFS.find((d) => d.name === name)?.slug;
}

export const RENEWAL_PRICES: Record<CertificationLevel, number> = LEVEL_RENEWAL;
export const CERTIFICATION_PRICES: Record<CertificationLevel, number> = LEVEL_PRICE;

export const CERTIFICATION_LEVELS = LEVELS;

/* ──────────────────────────────────────────────────────────── */
/* Seeded agency / enterprise plans                             */
/* ──────────────────────────────────────────────────────────── */

function buildAgencyPlans(): PricingLicense[] {
  const plans: PricingLicense[] = [];
  for (const ind of INDUSTRY_DEFS) {
    const prefix = ind.slug.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 3);
    plans.push({
      id: `license-${ind.slug}-agency`,
      type: "agency",
      name: `${ind.name} Agency (25 Seats)`,
      seats: 25,
      industry: ind.name,
      levels_covered: LEVELS,
      renewal_included: true,
      term: "annual",
      status: "active",
      price: 8950,
      currency: "$",
      price_plan_id: `wix-pp-${ind.slug}-agency`,
      start_date: "2026-01-01",
      end_date: "2026-12-31",
    });
    plans.push({
      id: `license-${ind.slug}-enterprise`,
      type: "enterprise",
      name: `${ind.name} Enterprise (25 Seats)`,
      seats: 25,
      industry: ind.name,
      levels_covered: LEVELS,
      renewal_included: true,
      term: "annual",
      status: "active",
      price: 14950,
      currency: "$",
      price_plan_id: `wix-pp-${ind.slug}-enterprise`,
      start_date: "2026-01-01",
      end_date: "2026-12-31",
    });
  }
  return plans;
}

export const AGENCY_ENTERPRISE_PLANS: PricingLicense[] = buildAgencyPlans();

/* ──────────────────────────────────────────────────────────── */
/* Seeded demo user + org + certification statuses              */
/* ──────────────────────────────────────────────────────────── */

export const DEMO_ORGANIZATION: Organization = {
  id: "org-demo-1",
  name: "Metro Police Department",
  type: "law_enforcement",
  seat_plans: ["license-law-enforcement-agency"],
  assigned_seats: {
    "license-law-enforcement-agency": ["user-1", "user-2", "user-3", "user-4", "user-5"],
  },
};

export const DEMO_SEAT_USERS: { id: string; name: string; email: string; role: UserRole; level: CertificationLevel; status: "active" | "pending" }[] = [
  { id: "user-1", name: "Sarah Chen", email: "s.chen@metropd.gov", role: "supervisor", level: "Supervisor", status: "active" },
  { id: "user-2", name: "Marcus Johnson", email: "m.johnson@metropd.gov", role: "student", level: "Professional", status: "active" },
  { id: "user-3", name: "Elena Rodriguez", email: "e.rodriguez@metropd.gov", role: "manager", level: "Manager", status: "active" },
  { id: "user-4", name: "David Kim", email: "d.kim@metropd.gov", role: "student", level: "Professional", status: "pending" },
  { id: "user-5", name: "Priya Patel", email: "p.patel@metropd.gov", role: "executive", level: "Executive", status: "active" },
];

export const DEMO_USER: UserMember = {
  id: "user-demo",
  name: "Alex Morgan",
  email: "alex.morgan@sentinel-architecture.com",
  role: "agency_admin",
  organization_id: "org-demo-1",
  active_licenses: ["license-law-enforcement-agency"],
  completed_certifications: ["law-enforcement-professional"],
  progress: {
    "law-enforcement-professional": {
      m1: { status: "completed", quiz_score: 92, capstone_status: "completed" },
      m2: { status: "completed", quiz_score: 88, capstone_status: "completed" },
      m3: { status: "completed", quiz_score: 90, capstone_status: "completed" },
    },
    "law-enforcement-supervisor": {
      m1: { status: "completed", quiz_score: 85 },
      m2: { status: "in_progress" },
      m3: { status: "not_started" },
    },
  },
};

export const DEMO_CERT_STATUSES: CertificationStatusRecord[] = [
  {
    user_id: "user-demo",
    program_id: "law-enforcement-professional",
    status: "completed",
    exam_score: 92,
    capstone_status: "completed",
    certificate_issued: true,
    badge_issued: true,
    renewal_due_date: "2027-01-15",
    completion_date: "2026-01-15",
    credential_code: "LEP-C",
  },
  {
    user_id: "user-demo",
    program_id: "law-enforcement-supervisor",
    status: "in_progress",
    exam_score: 85,
    capstone_status: "not_started",
    certificate_issued: false,
    badge_issued: false,
    renewal_due_date: "2027-06-01",
  },
  {
    user_id: "user-demo",
    program_id: "healthcare-professional",
    status: "expired",
    exam_score: 78,
    capstone_status: "completed",
    certificate_issued: true,
    badge_issued: true,
    renewal_due_date: "2026-03-01",
    completion_date: "2025-03-01",
    credential_code: "HEP-C",
  },
];

/* ──────────────────────────────────────────────────────────── */
/* Admin metrics                                                */
/* ──────────────────────────────────────────────────────────── */

export const ADMIN_METRICS = {
  totalUsers: 1284,
  totalActiveCertifications: 892,
  totalLicenses: 47,
  seatUtilization: [
    { industry: "Law Enforcement", used: 18, total: 25 },
    { industry: "Healthcare", used: 22, total: 25 },
    { industry: "Education", used: 15, total: 25 },
    { industry: "Manufacturing", used: 10, total: 25 },
    { industry: "Transportation", used: 20, total: 25 },
  ],
  renewalRate: 0.87,
};

/* ──────────────────────────────────────────────────────────── */
/* Helpers                                                     */
/* ──────────────────────────────────────────────────────────── */

export function getProgramById(id: string): CertificationProgram | undefined {
  return CERTIFICATION_PROGRAMS.find((p) => p.id === id);
}

export function getAgencyPlanById(id: string): PricingLicense | undefined {
  return AGENCY_ENTERPRISE_PLANS.find((p) => p.id === id);
}

export function getCertStatus(programId: string): CertificationStatusRecord | undefined {
  return DEMO_CERT_STATUSES.find((c) => c.program_id === programId);
}

export function levelColor(level: CertificationLevel): string {
  switch (level) {
    case "Professional": return "var(--primary-500)";
    case "Supervisor": return "var(--accent-500)";
    case "Manager": return "var(--warning-500)";
    case "Executive": return "#8b5cf6";
  }
}

export function statusColor(status: ProgramStatus | LicenseStatus): string {
  switch (status) {
    case "completed": case "active": return "var(--accent-500)";
    case "in_progress": return "var(--primary-500)";
    case "not_started": return "var(--neutral-400)";
    case "expired": return "var(--error-500)";
  }
}
